import { useQuery } from '@tanstack/react-query';
import { CloudLightning } from 'lucide-react';
import { RelativeTime } from '../../components/ui/Time.js';
import { cx } from '../../lib/cx.js';
import { policiesApi } from '../../lib/api.js';
import { formatCount, formatMinutes } from '../../lib/format.js';
import type { ActiveStorm } from '../../lib/schemas.js';

/** Refresh often enough that a storm is visible while it matters. */
const POLL_MS = 20_000;

const STORMS_KEY = ['policies', 'storms'] as const;

/**
 * "The storm guard is holding messages."
 *
 * During a flood the guard pages once and holds the rest, so the console goes
 * quiet exactly when it looks most alarming. Without this banner an operator
 * has no way to tell a working guard from a broken notification pipeline, so
 * it is deliberately loud: the warning tone, the policy and integration by
 * name, and how many alarms are waiting for the summary.
 */
export function StormBanner({ className }: { className?: string }) {
  const storms = useQuery({
    queryKey: STORMS_KEY,
    queryFn: () => policiesApi.storms(),
    refetchInterval: POLL_MS,
    // A console that cannot reach this endpoint should simply not show the
    // banner; it is never the reason a page fails.
    retry: false,
  });
  const items = storms.data ?? [];
  if (items.length === 0) return null;

  return (
    <div
      role="status"
      className={cx(
        'flex flex-col gap-2 rounded-[var(--radius-md)] border px-3 py-2.5',
        'border-[var(--warn)] bg-[var(--warn-soft)]',
        className,
      )}
    >
      <div className="flex items-start gap-2.5">
        <span className="text-[var(--warn)]">
          <CloudLightning className="mt-0.5 h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[var(--text-base)] font-medium text-[var(--warn)]">
            {items.length === 1
              ? 'The storm guard is holding notifications'
              : `The storm guard is holding notifications on ${items.length} policies`}
          </p>
          <p className="text-[var(--text-base)] text-[var(--text-2)]">
            Everyone was paged once when the storm started. One summary follows when it subsides.
          </p>
        </div>
      </div>
      <ul className="flex flex-col gap-1 pl-[26px]">
        {items.map((storm) => (
          <li
            key={storm.storm_id}
            className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[var(--text-sm)] text-[var(--text-2)]"
          >
            <span className="font-medium text-[var(--text)]">{storm.policy_name}</span>
            <span className="text-[var(--text-3)]">·</span>
            <span>{storm.integration_name}</span>
            <span className="text-[var(--text-3)]">·</span>
            <span className="tnum">{describeStorm(storm)}</span>
            <span className="text-[var(--text-3)]">·</span>
            <span>
              started <RelativeTime value={storm.started_at} withSuffix />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** `18 alarms held · 31 in 5 min, trips at 25` — the guard explaining itself. */
function describeStorm(storm: ActiveStorm): string {
  const held = `${formatCount(storm.held)} alarm${storm.held === 1 ? '' : 's'} held`;
  if (!storm.threshold || !storm.window_minutes) return held;
  return `${held} · ${formatCount(storm.rate)} in ${formatMinutes(storm.window_minutes)}, trips at ${formatCount(storm.threshold)}`;
}
